import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import {
	getFilteringByKey,
	getFilteringParametersCount,
} from "../../selectors";
import {
	resetFilteringState,
	setFilteringState,
} from "../../store/filtering/actions";

const FilterContext = createContext();

export const FilterProvider = ({ children, defaultIsOpen = false }) => {
	const dispatch = useDispatch();

	const filterCount = useSelector(getFilteringParametersCount);

	const [filteringParameters, setFilteringParameters] = useState({});
	const [isOpen, setIsOpen] = useState(defaultIsOpen);

	const toggle = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	const applyFilters = useCallback(() => {
		toggle();
		dispatch(setFilteringState(filteringParameters));
	}, [filteringParameters, dispatch, toggle]);

	const clearFilters = useCallback(() => {
		setFilteringParameters({});
		toggle();
		dispatch(resetFilteringState());
	}, [dispatch, toggle]);

	const value = useMemo(
		() => ({
			isOpen,
			toggle,
			filterCount,
			filteringParameters,
			setFilteringParameters,
			applyFilters,
			clearFilters,
		}),
		[
			isOpen,
			toggle,
			filterCount,
			filteringParameters,
			setFilteringParameters,
			applyFilters,
			clearFilters,
		],
	);

	return (
		<FilterContext.Provider value={value}>{children}</FilterContext.Provider>
	);
};

FilterProvider.propTypes = {
	children: PropTypes.node.isRequired,
	defaultIsOpen: PropTypes.bool,
};

export const useFilter = () => {
	const context = useContext(FilterContext);
	if (!context) {
		throw new Error("useFilter must be used within FilterProvider");
	}

	return context;
};

export const useMobileFilterSelect = (filteringControl) => {
	const { filteringKey, options } = filteringControl;
	const { filteringParameters, setFilteringParameters } = useFilter();
	const selectedOption =
		useSelector((state) => getFilteringByKey(state, { filteringKey })) ??
		"default";

	const [localSelectedOption, setLocalSelectedOption] = useState(null);
	const [isOpen, setIsOpen] = useState(false);

	const toggle = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	const handleChange = useCallback(
		(event) => {
			const { value } = event.target;
			if (!value) return;

			const formattedValue = getFormattedValue(value);

			setLocalSelectedOption(formattedValue);
			setFilteringParameters((prev) => ({
				...prev,
				[filteringKey]: formattedValue,
			}));
			toggle();
		},
		[filteringKey, setFilteringParameters, setLocalSelectedOption, toggle],
	);

	const localDisplayOption = useMemo(() => {
		if (localSelectedOption) return localSelectedOption;

		return selectedOption;
	}, [selectedOption, localSelectedOption]);
	const displayValue = useMemo(
		() => options.find((option) => option.value === localDisplayOption)?.label,
		[options, localDisplayOption],
	);
	const displayIndicator = useMemo(() => {
		if (localSelectedOption) return false;
		if (selectedOption !== "default") return true;

		return false;
	}, [localSelectedOption, selectedOption]);

	const finalSelectedOption = useMemo(() => {
		if (filteringParameters[filteringKey])
			return filteringParameters[filteringKey];

		return selectedOption;
	}, [filteringParameters, filteringKey, selectedOption]);

	return {
		isOpen,
		toggle,
		filteringParameters,
		selectedOption: finalSelectedOption,
		localSelectedOption,
		handleChange,
		displayIndicator,
		displayValue,
	};
};

export const useDesktopFilterSelect = (
	filteringKey,
	defaultValue = "default",
) => {
	const selectedOption =
		useSelector((state) => getFilteringByKey(state, { filteringKey })) ??
		defaultValue;

	const dispatch = useDispatch();
	const handleChange = useCallback(
		(event) => {
			const { value } = event.target;
			if (!value) return;

			const formattedValue = getFormattedValue(value);

			dispatch(setFilteringState({ [filteringKey]: formattedValue }));
		},
		[filteringKey, dispatch],
	);

	return { selectedOption, handleChange };
};

const getFormattedValue = (value) => {
	const numberValue = Number(value);
	const isNumber = !Number.isNaN(numberValue);

	return isNumber ? numberValue : value;
};
